import { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Package, Search, Truck, CheckCircle } from "lucide-react";

const STATUS_MAP = {
  PAID: { label: "Bezahlt", color: "text-green-600", desc: "Zahlung eingegangen, Seller bereitet Versand vor." },
  SHIPPED: { label: "Versendet", color: "text-blue-600", desc: "Artikel wurde versendet." },
  DELIVERED: { label: "Zugestellt", color: "text-green-600", desc: "Artikel zugestellt." },
  COMPLETED: { label: "Abgeschlossen", color: "text-[#999]", desc: "Bestellung abgeschlossen." },
};

export default function GuestTrackingPage() {
  const { t } = useTranslation();
  const [token, setToken] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const track = async () => {
    if (!token.trim()) return;
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.get(`/orders/guest/${token.trim()}`);
      setOrder(data);
    } catch {
      setError(t("guest_tracking.not_found"));
    }
    finally { setLoading(false); }
  };

  const s = order ? (STATUS_MAP[order.status] || { label: order.status, color: "text-[#999]", desc: "" }) : null;

  return (
    <div data-testid="guest-tracking-page" className="px-4 py-8 max-w-md mx-auto">
      <div className="text-center mb-6">
        <Package size={32} className="mx-auto text-[#999] mb-3" />
        <h1 className="text-lg font-bold text-[#0A0A0A]">{t("guest_tracking.title")}</h1>
        <p className="text-sm text-[#999] mt-1">{t("guest_tracking.hint")}</p>
      </div>

      <div className="flex gap-2">
        <input data-testid="tracking-input" value={token} onChange={(e) => setToken(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && track()}
          className="flex-1 h-11 px-4 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
          placeholder="Tracking-Code eingeben..." />
        <button data-testid="tracking-search" onClick={track} disabled={loading || !token.trim()}
          className="rounded-full h-11 px-4 bg-[#0A0A0A] text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-30">
          <Search size={14} /> {loading ? "..." : "Suchen"}
        </button>
      </div>

      {error && <p data-testid="tracking-error" className="mt-4 text-sm text-red-500 text-center">{error}</p>}

      {order && (
        <div data-testid="tracking-result" className="mt-6 border border-[#E5E5E5] divide-y divide-[#E5E5E5]">
          <div className="p-4">
            <p className="text-sm font-bold text-[#0A0A0A]">{order.title}</p>
            <p className="text-xs text-[#999] mt-0.5">Bestell-Nr: {order.order_id}</p>
            <p className="text-base font-bold text-[#0A0A0A] mt-2">{order.price?.toFixed(2)} {order.currency}</p>
          </div>
          <div className="p-4 flex items-center gap-3">
            {order.status === "SHIPPED" ? <Truck size={20} className="text-blue-600" /> :
             order.status === "DELIVERED" || order.status === "COMPLETED" ? <CheckCircle size={20} className="text-green-600" /> :
             <Package size={20} className="text-amber-500" />}
            <div>
              <p className={`text-sm font-medium ${s.color}`}>{s.label}</p>
              <p className="text-xs text-[#999] mt-0.5">{s.desc}</p>
            </div>
          </div>
          <div className="p-4 text-xs text-[#999]">
            <p>Bestellt am: {new Date(order.created_at).toLocaleDateString("de-DE")}</p>
            {order.shipped_at && <p>Versendet am: {new Date(order.shipped_at).toLocaleDateString("de-DE")}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
